import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { Slider, Box, Button } from '@mui/material';
import { RestartAlt } from '@mui/icons-material';
import * as d3 from 'd3';

// Clamp a 1-D [lo, hi] domain inside [bLo, bHi], preserving span where possible
// and enforcing a minimum span so the axis never collapses to a single value.
const clampXDomain = ([lo, hi], [bLo, bHi]) => {
  const outerSpan = bHi - bLo;
  const minSpan = Math.min(0.02, outerSpan || 0.02);
  let span = hi - lo;
  if (span < minSpan) span = minSpan;
  if (span >= outerSpan) return [bLo, bHi];
  let nLo = lo;
  let nHi = lo + span;
  if (nLo < bLo) { nLo = bLo; nHi = bLo + span; }
  if (nHi > bHi) { nHi = bHi; nLo = bHi - span; }
  return [nLo, nHi];
};

const HistogramChart = forwardRef(({ data, yAxisLabel, height = 500, showCutoffLines = false, cleanHistogramTitle }, ref) => {
  const svgRef = useRef();
  const containerRef = useRef();
  const tooltipRef = useRef();
  const metricsRef = useRef(null);
  const [width, setWidth] = useState(800);
  // User-driven x zoom domain; null means "full data range". Owned here so the
  // wheel, drag, and slider all stay in sync and Explore/modals inherit zoom.
  const [userXDomain, setUserXDomain] = useState(null);

  // Full x extent of the data (expanded to include cutoff lines when shown).
  const dataDomain = React.useMemo(() => {
    if (!data || !Array.isArray(data.values) || data.values.length === 0) return [0, 100];
    let min = Math.min(...data.values);
    let max = Math.max(...data.values);
    if (showCutoffLines && data.cutoffValues) {
      if (data.cutoffValues.normalFloor !== undefined && data.cutoffValues.normalFloor !== null) {
        min = Math.min(min, data.cutoffValues.normalFloor);
      }
      if (data.cutoffValues.normalCeiling !== undefined && data.cutoffValues.normalCeiling !== null) {
        max = Math.max(max, data.cutoffValues.normalCeiling);
      }
    }
    if (max <= min) max = min + 1; // guard against a zero-width slider domain
    return [min, max];
  }, [data?.values, data?.cutoffValues, showCutoffLines]);

  // Effective (rendered) x domain. Always defined so binning matches prior behavior.
  const effectiveDomain = userXDomain || dataDomain;
  const sliderStep = Math.max((dataDomain[1] - dataDomain[0]) / 100, 0.0001);

  // Reset zoom whenever the underlying data or cutoff visibility changes.
  useEffect(() => {
    setUserXDomain(null);
  }, [data, showCutoffLines]);

    useImperativeHandle(ref, () => ({
    exportAsPNG: () => {
        const svgNode = svgRef.current;
        if (!svgNode) return;

        // Get CSS variable colors
        const styles = getComputedStyle(document.documentElement);
        const primaryColor = styles.getPropertyValue('--primary-color').trim() || '#006778';
        const accentPrimaryColor = styles.getPropertyValue('--accent-primary-color').trim() || '#bb4d00';
        const infoColor = styles.getPropertyValue('--info-color').trim() || '#4173B4';

        // Serialize SVG and replace CSS variables with actual colors
        const serializer = new XMLSerializer();
        let svgString = serializer.serializeToString(svgNode);

        // Add font style
        const styleTag = `<style>
        text { font-family: "Open Sans", "Segoe UI", "Helvetica Neue", Arial, sans-serif; }
        </style>`;
        svgString = svgString.replace(/<svg([^>]*)>/, `<svg$1>${styleTag}`);
        svgString = svgString.replace(/var\(--primary-color\)/g, primaryColor);
        svgString = svgString.replace(/var\(--accent-primary-color\)/g, accentPrimaryColor);
        svgString = svgString.replace(/var\(--info-color\)/g, infoColor);

        // Chart title - use cleanHistogramTitle if provided, otherwise use data.histogramTitle
        const plotTitle = cleanHistogramTitle || data?.histogramTitle || 'Selected Analyte';

        // Font and layout
        const appFont = '"Open Sans", "Segoe UI", "Helvetica Neue", Arial, sans-serif';
        const lineHeight = 28;
        const titleHeight = lineHeight + 10;

        // Set canvas size
        const svgWidth = svgNode.width.baseVal.value;
        const svgHeight = svgNode.height.baseVal.value;
        const canvas = document.createElement('canvas');
        canvas.width = svgWidth;
        canvas.height = svgHeight + titleHeight;
        const ctx = canvas.getContext('2d');

        // Fill white background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Draw title
        ctx.font = `20px ${appFont}`;
        ctx.fillStyle = '#000000';
        ctx.textAlign = 'center';
        ctx.fillText(plotTitle, canvas.width / 2, 32);

        // Draw chart below title
        const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);
        const img = new window.Image();

        img.onload = () => {
        ctx.drawImage(img, 0, titleHeight);
        URL.revokeObjectURL(url);

        const pngUrl = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = pngUrl;
        link.download = `ED3N-Histogram-Chart-${plotTitle.replace(/\s+/g, '_')}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        };

        img.src = url;
    }
    }));

    useEffect(() => {
        if (!containerRef.current) return;
        const handleResize = () => {
            if (!containerRef.current) return;
            setWidth(containerRef.current.offsetWidth || 800);
        };
        handleResize();
        const resizeObserver = new window.ResizeObserver(handleResize);
        resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect();
    }, []);

    const values = React.useMemo(() => {
        if (!data || !Array.isArray(data.values)) return [];
        if (!userXDomain) return data.values;
        return data.values.filter(v => v >= userXDomain[0] && v <= userXDomain[1]);
    }, [data?.values, userXDomain]);

    // Draw chart
    useEffect(() => {
        // Early return if no data to prevent accessing null svgRef
        if (!data || !Array.isArray(data.values) || data.values.length === 0) return;

        // Effective x zoom range (always defined). Mirrors the prior parent-driven
        // behavior where a full-range zoomRange was always supplied.
        const zoomRange = userXDomain || dataDomain;

        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();

        const margin = { top: 50, right: 30, bottom: 60, left: 70 };
        const plotWidth = width - margin.left - margin.right;
        const plotHeight = height - margin.top - margin.bottom;

        // Calculate data range
        const minValue = Math.min(...data.values);
        const maxValue = Math.max(...data.values);
        const dataRange = maxValue - minValue;

        // Expand range to include cutoff values if they're enabled and exist
        let adjustedMinValue = minValue;
        let adjustedMaxValue = maxValue;
        
        if (showCutoffLines && data.cutoffValues) {
            if (data.cutoffValues.normalFloor !== undefined && data.cutoffValues.normalFloor !== null) {
                adjustedMinValue = Math.min(adjustedMinValue, data.cutoffValues.normalFloor);
            }
            if (data.cutoffValues.normalCeiling !== undefined && data.cutoffValues.normalCeiling !== null) {
                adjustedMaxValue = Math.max(adjustedMaxValue, data.cutoffValues.normalCeiling);
            }
        }

        // Use adjusted range for calculations if cutoffs extend beyond data
        const effectiveMinValue = adjustedMinValue;
        const effectiveMaxValue = adjustedMaxValue;
        const effectiveDataRange = effectiveMaxValue - effectiveMinValue;

        // Determine MINIMUM bin width based on overall data range
        // This prevents over-zooming on large datasets
        let minBinWidth;
        if (effectiveDataRange < 2) {
            minBinWidth = 0.01; // Can zoom to hundredths for tiny ranges (0-2)
        } else if (effectiveDataRange < 10) {
            minBinWidth = 0.1; // Can zoom to tenths for small ranges (2-10)
        } else if (effectiveDataRange < 50) {
            minBinWidth = 0.5; // Can zoom to halves for medium ranges (10-50)
        } else if (effectiveDataRange < 200) {
            minBinWidth = 1; // Can zoom to ones for larger ranges (50-200)
        } else {
            minBinWidth = 5; // Can zoom to fives for very large ranges (200+)
        }

        // Determine appropriate bin width based on data range (for initial view)
        let binWidth;
        if (effectiveDataRange < 2) {
            binWidth = 0.01;
        } else if (effectiveDataRange < 20) {
            binWidth = 0.1;
        } else if (effectiveDataRange < 200) {
            binWidth = 1;
        } else {
            binWidth = Math.ceil(effectiveDataRange / 100);
        }

        // When zoomed, use finer resolution with more granular steps
        // BUT respect the minimum bin width based on data range
        if (zoomRange) {
            const zoomRangeSize = zoomRange[1] - zoomRange[0];
            
            // More granular bin width selection based on zoom range
            if (zoomRangeSize < 0.5) {
                binWidth = Math.max(minBinWidth, 0.01);
            } else if (zoomRangeSize < 1) {
                binWidth = Math.max(minBinWidth, 0.02);
            } else if (zoomRangeSize < 2) {
                binWidth = Math.max(minBinWidth, 0.05);
            } else if (zoomRangeSize < 5) {
                binWidth = Math.max(minBinWidth, 0.1);
            } else if (zoomRangeSize < 10) {
                binWidth = Math.max(minBinWidth, 0.2);
            } else if (zoomRangeSize < 20) {
                binWidth = Math.max(minBinWidth, 0.5);
            } else if (zoomRangeSize < 40) {
                binWidth = Math.max(minBinWidth, 1);
            } else if (zoomRangeSize < 80) {
                binWidth = Math.max(minBinWidth, 2);
            } else if (zoomRangeSize < 150) {
                binWidth = Math.max(minBinWidth, 5);
            } else if (zoomRangeSize < 300) {
                binWidth = Math.max(minBinWidth, 10);
            } else {
                binWidth = Math.max(minBinWidth, Math.ceil(zoomRangeSize / 100));
            }
        }

        // Round min/max to bin width boundaries (use effective values that include cutoffs)
        const roundedMin = Math.floor(effectiveMinValue / binWidth) * binWidth;
        const roundedMax = Math.ceil(effectiveMaxValue / binWidth) * binWidth;

        // Generate bin edges
        const binEdges = d3.range(roundedMin, roundedMax + binWidth, binWidth);

        // Bin the full dataset
        const allBins = d3.bin()
            .domain([roundedMin, roundedMax])
            .thresholds(binEdges)
            (data.values);

        // Filter bins by zoom range
        const bins = zoomRange
            ? allBins.filter(bin => bin.x1 > zoomRange[0] && bin.x0 < zoomRange[1])
            : allBins;

        if (bins.length === 0) return;

        // X scale domain
        const xDomain = zoomRange || [roundedMin, roundedMax];
        const x = d3.scaleLinear()
            .domain(xDomain)
            .range([0, plotWidth]);

        // Y scale (add ~10% headroom so the tallest bar isn't clipped at the top)
        const yMax = d3.max(bins, d => d.length) || 1;
        const y = d3.scaleLinear()
            .domain([0, yMax * 1.1])
            .nice()
            .range([plotHeight, 0]);

        const g = svg.append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        // Horizontal grid lines
        g.append('g')
            .attr('class', 'grid')
            .attr('opacity', 0.1)
            .call(d3.axisLeft(y)
                .tickSize(-plotWidth)
                .tickFormat(''));

        // Clip path
        svg.append('defs')
            .append('clipPath')
            .attr('id', 'plot-clip')
            .append('rect')
            .attr('x', 0)
            .attr('y', 0)
            .attr('width', plotWidth)
            .attr('height', plotHeight);

        // Bars with clipping
        const barsGroup = g.append('g')
            .attr('clip-path', 'url(#plot-clip)');

        barsGroup.selectAll('rect')
            .data(bins)
            .enter()
            .append('rect')
            .attr('x', d => x(d.x0))
            .attr('y', d => y(d.length))
            .attr('width', d => Math.max(0, x(d.x1) - x(d.x0) - 2))
            .attr('height', d => plotHeight - y(d.length))
            .attr('fill', 'var(--primary-color)')
            .attr('stroke', '#333')
            .attr('rx', 2);

        // Patient value line
        if (
            data.patientValue !== undefined &&
            data.patientValue !== null &&
            (!zoomRange || (data.patientValue >= zoomRange[0] && data.patientValue <= zoomRange[1]))
        ) {
            const patientX = x(data.patientValue);
            if (patientX >= 0 && patientX <= plotWidth) {
                g.append('line')
                    .attr('class', 'patient-value-line')
                    .attr('x1', patientX)
                    .attr('x2', patientX)
                    .attr('y1', 0)
                    .attr('y2', plotHeight)
                    .attr('stroke', '#fff')
                    .attr('stroke-width', 7);

                g.append('line')
                    .attr('class', 'patient-value-line')
                    .attr('x1', patientX)
                    .attr('x2', patientX)
                    .attr('y1', 0)
                    .attr('y2', plotHeight)
                    .attr('stroke', 'var(--accent-primary-color)')
                    .attr('stroke-width', 5)
                    .attr('stroke-dasharray', '8,4');
            }
        }

        // Cutoff lines
        const cutoffLines = [];
        if (showCutoffLines && data.cutoffValues) {
            // Normal floor line
            if (
                data.cutoffValues.normalFloor !== undefined &&
                data.cutoffValues.normalFloor !== null &&
                (!zoomRange || (data.cutoffValues.normalFloor >= zoomRange[0] && data.cutoffValues.normalFloor <= zoomRange[1]))
            ) {
                const floorX = x(data.cutoffValues.normalFloor);
                if (floorX >= 0 && floorX <= plotWidth) {
                    // White solid backing line
                    g.append('line')
                        .attr('class', 'cutoff-line-floor-backing')
                        .attr('x1', floorX)
                        .attr('x2', floorX)
                        .attr('y1', 0)
                        .attr('y2', plotHeight)
                        .attr('stroke', '#fff')
                        .attr('stroke-width', 7);

                    // Colored dotted line
                    g.append('line')
                        .attr('class', 'cutoff-line-floor')
                        .attr('x1', floorX)
                        .attr('x2', floorX)
                        .attr('y1', 0)
                        .attr('y2', plotHeight)
                        .attr('stroke', 'var(--info-color)')
                        .attr('stroke-width', 5)
                        .attr('stroke-linecap', 'round')
                        .attr('stroke-dasharray', '1,10');
                    cutoffLines.push({ type: 'floor', value: data.cutoffValues.normalFloor });
                }
            }
            
            // Normal ceiling line
            if (
                data.cutoffValues.normalCeiling !== undefined &&
                data.cutoffValues.normalCeiling !== null &&
                (!zoomRange || (data.cutoffValues.normalCeiling >= zoomRange[0] && data.cutoffValues.normalCeiling <= zoomRange[1]))
            ) {
                const ceilingX = x(data.cutoffValues.normalCeiling);
                if (ceilingX >= 0 && ceilingX <= plotWidth) {
                    // White solid backing line
                    g.append('line')
                        .attr('class', 'cutoff-line-ceiling-backing')
                        .attr('x1', ceilingX)
                        .attr('x2', ceilingX)
                        .attr('y1', 0)
                        .attr('y2', plotHeight)
                        .attr('stroke', '#fff')
                        .attr('stroke-width', 7);

                    // Colored dotted line
                    g.append('line')
                        .attr('class', 'cutoff-line-ceiling')
                        .attr('x1', ceilingX)
                        .attr('x2', ceilingX)
                        .attr('y1', 0)
                        .attr('y2', plotHeight)
                        .attr('stroke', 'var(--info-color)')
                        .attr('stroke-width', 5)
                        .attr('stroke-linecap', 'round')
                        .attr('stroke-dasharray', '1,10');
                    cutoffLines.push({ type: 'ceiling', value: data.cutoffValues.normalCeiling });
                }
            }
        }

        // Tooltips for bars
        barsGroup.selectAll('rect')
            .on('mousemove', function(event, d) {
                const [xPos, yPos] = d3.pointer(event, containerRef.current);
                const tooltip = tooltipRef.current;
                tooltip.style.display = 'block';
                
                // Set content first to get accurate dimensions
                const decimals = binWidth < 1 ? 2 : 0;
                tooltip.innerHTML = `
                    <strong>Range:</strong> ${d.x0.toFixed(decimals)} – ${d.x1.toFixed(decimals)}<br/>
                    <strong>Number of Patients:</strong> ${d.length}
                `;
                
                // Check if tooltip would overflow to the right
                const tooltipWidth = tooltip.offsetWidth;
                const containerWidth = containerRef.current.offsetWidth;
                const rightEdge = xPos + 10 + tooltipWidth;
                
                if (rightEdge > containerWidth) {
                    // Position to the left of cursor
                    tooltip.style.left = `${xPos - tooltipWidth - 10}px`;
                } else {
                    // Position to the right of cursor
                    tooltip.style.left = `${xPos + 10}px`;
                }
                tooltip.style.top = `${yPos - 10}px`;
            })
            .on('mouseleave', function() {
                tooltipRef.current.style.display = 'none';
            });

        // Patient value line tooltip
        if (data.patientValue !== undefined && data.patientValue !== null) {
            g.selectAll('.patient-value-line')
                .on('mousemove', function(event) {
                    const [xPos, yPos] = d3.pointer(event, containerRef.current);
                    const tooltip = tooltipRef.current;
                    tooltip.style.display = 'block';
                    
                    // Set content first to get accurate dimensions
                    tooltip.innerHTML = `
                        <strong>Patient Test Result:</strong> ${data.patientValue} (μmol/L)
                    `;
                    
                    // Check if tooltip would overflow to the right
                    const tooltipWidth = tooltip.offsetWidth;
                    const containerWidth = containerRef.current.offsetWidth;
                    const rightEdge = xPos + 10 + tooltipWidth;
                    
                    if (rightEdge > containerWidth) {
                        // Position to the left of cursor
                        tooltip.style.left = `${xPos - tooltipWidth - 10}px`;
                    } else {
                        // Position to the right of cursor
                        tooltip.style.left = `${xPos + 10}px`;
                    }
                    tooltip.style.top = `${yPos - 10}px`;
                })
                .on('mouseleave', function() {
                    tooltipRef.current.style.display = 'none';
                });
        }

        // Cutoff line tooltips
        if (showCutoffLines && data.cutoffValues) {
            if (data.cutoffValues.normalFloor !== undefined && data.cutoffValues.normalFloor !== null) {
                g.selectAll('.cutoff-line-floor, .cutoff-line-floor-backing')
                    .on('mousemove', function(event) {
                        const [xPos, yPos] = d3.pointer(event, containerRef.current);
                        const tooltip = tooltipRef.current;
                        tooltip.style.display = 'block';
                        
                        // Set content first to get accurate dimensions
                        tooltip.innerHTML = `
                            <strong>Normal Floor:</strong> ${data.cutoffValues.normalFloor} (μmol/L)
                        `;
                        
                        // Check if tooltip would overflow to the right
                        const tooltipWidth = tooltip.offsetWidth;
                        const containerWidth = containerRef.current.offsetWidth;
                        const rightEdge = xPos + 10 + tooltipWidth;
                        
                        if (rightEdge > containerWidth) {
                            // Position to the left of cursor
                            tooltip.style.left = `${xPos - tooltipWidth - 10}px`;
                        } else {
                            // Position to the right of cursor
                            tooltip.style.left = `${xPos + 10}px`;
                        }
                        tooltip.style.top = `${yPos - 10}px`;
                    })
                    .on('mouseleave', function() {
                        tooltipRef.current.style.display = 'none';
                    });
            }
            
            if (data.cutoffValues.normalCeiling !== undefined && data.cutoffValues.normalCeiling !== null) {
                g.selectAll('.cutoff-line-ceiling, .cutoff-line-ceiling-backing')
                    .on('mousemove', function(event) {
                        const [xPos, yPos] = d3.pointer(event, containerRef.current);
                        const tooltip = tooltipRef.current;
                        tooltip.style.display = 'block';
                        
                        // Set content first to get accurate dimensions
                        tooltip.innerHTML = `
                            <strong>Normal Ceiling:</strong> ${data.cutoffValues.normalCeiling} (μmol/L)
                        `;
                        
                        // Check if tooltip would overflow to the right
                        const tooltipWidth = tooltip.offsetWidth;
                        const containerWidth = containerRef.current.offsetWidth;
                        const rightEdge = xPos + 10 + tooltipWidth;
                        
                        if (rightEdge > containerWidth) {
                            // Position to the left of cursor
                            tooltip.style.left = `${xPos - tooltipWidth - 10}px`;
                        } else {
                            // Position to the right of cursor
                            tooltip.style.left = `${xPos + 10}px`;
                        }
                        tooltip.style.top = `${yPos - 10}px`;
                    })
                    .on('mouseleave', function() {
                        tooltipRef.current.style.display = 'none';
                    });
            }
        }

        // Dynamic tick marks based on zoom level and bin width
        const visibleRange = xDomain[1] - xDomain[0];
        let tickInterval;
        
        if (visibleRange <= 0.1) {
            tickInterval = 0.01; // Show every 0.01 when zoomed to <= 0.1
        } else if (visibleRange <= 0.3) {
            tickInterval = 0.02; // Show every 0.02 when zoomed to <= 0.3
        } else if (visibleRange < 1) {
            tickInterval = 0.05; // Show every 0.05 when zoomed to < 1
        } else if (visibleRange < 10) {
            tickInterval = 0.5; // Show every 0.5 when zoomed to < 10
        } else if (visibleRange < 50) {
            tickInterval = 5; // Show every 5 when range < 50
        } else if (visibleRange < 200) {
            tickInterval = 20; // Show every 20 when range < 200
        } else {
            tickInterval = Math.ceil(visibleRange / 10); // ~10 ticks for large ranges
        }

        // Generate tick values
        const tickStart = Math.ceil(xDomain[0] / tickInterval) * tickInterval;
        const tickEnd = Math.floor(xDomain[1] / tickInterval) * tickInterval;
        const xTickValues = d3.range(tickStart, tickEnd + tickInterval, tickInterval);

        // Determine tick format based on tick interval
        const tickDecimals = tickInterval < 1 ? 2 : (tickInterval < 10 ? 1 : 0);

        // X axis
        g.append('g')
            .attr('transform', `translate(0,${plotHeight})`)
            .call(
                d3.axisBottom(x)
                    .tickValues(xTickValues)
                    .tickFormat(d => d.toFixed(tickDecimals))
            )
            .selectAll('.tick text')
            .attr('font-size', '14px')
            .attr('font-family', '"Open Sans", "Segoe UI", "Helvetica Neue", Arial, sans-serif');

        // Y axis
        g.append('g')
            .call(d3.axisLeft(y))
            .selectAll('.tick text')
            .attr('font-size', '14px')
            .attr('font-family', '"Open Sans", "Segoe UI", "Helvetica Neue", Arial, sans-serif');

        // X label
        svg.append('text')
            .attr('x', margin.left + plotWidth / 2)
            .attr('y', height - 5)
            .attr('text-anchor', 'middle')
            .attr('font-size', '16px')
            .attr('font-family', '"Open Sans", "Segoe UI", "Helvetica Neue", Arial, sans-serif')
            .text(data.xAxisLabel || '');

        // Y label
        svg.append('text')
            .attr('transform', 'rotate(-90)')
            .attr('x', -height / 2)
            .attr('y', 18)
            .attr('text-anchor', 'middle')
            .attr('font-size', '16px')
            .attr('font-family', '"Open Sans", "Segoe UI", "Helvetica Neue", Arial, sans-serif')
            .text(yAxisLabel || 'Number of Patients');
        
        // Patient Value Legend (bottom right)
        const patientLegendWidth = 172;
        const patientLegendHeight = 24;
        const legendMargin = 5;
        const accentPrimaryColor = 'var(--accent-primary-color)' || '#bb4d00';

        const patientLegend = svg.append('g')
            .attr('class', 'legend-patient')
            .attr('transform', `translate(${width - patientLegendWidth - legendMargin}, ${height - patientLegendHeight - legendMargin + 4})`);

        patientLegend.append('line')
            .attr('x1', 16)
            .attr('x2', 64)
            .attr('y1', patientLegendHeight / 2)
            .attr('y2', patientLegendHeight / 2)
            .attr('stroke', '#fff')
            .attr('stroke-width', 7);

        patientLegend.append('line')
            .attr('x1', 16)
            .attr('x2', 64)
            .attr('y1', patientLegendHeight / 2)
            .attr('y2', patientLegendHeight / 2)
            .attr('stroke', accentPrimaryColor)
            .attr('stroke-width', 4)
            .attr('stroke-dasharray', '8,4');

        patientLegend.append('text')
            .attr('x', 74)
            .attr('y', patientLegendHeight / 2 + 5)
            .attr('font-size', '14px')
            .attr('fill', '#333')
            .style('font-family', 'Open Sans, Segoe UI, Helvetica Neue, Arial, sans-serif')
            .text('Patient Value');

        // Cutoff Lines Legend (bottom left) - only show if cutoff data exists and showCutoffLines is true
        if (showCutoffLines && data.cutoffValues && (data.cutoffValues.normalFloor !== undefined && data.cutoffValues.normalFloor !== null || data.cutoffValues.normalCeiling !== undefined && data.cutoffValues.normalCeiling !== null)) {
            const cutoffLegendWidth = 150;
            const cutoffLegendHeight = 24;
            const infoColor = 'var(--info-color)' || '#4173B4';

            const cutoffLegend = svg.append('g')
                .attr('class', 'legend-cutoff')
                .attr('transform', `translate(${margin.left}, ${height - cutoffLegendHeight - legendMargin + 4})`);

            cutoffLegend.append('line')
                .attr('x1', 12)
                .attr('x2', 52)
                .attr('y1', cutoffLegendHeight / 2)
                .attr('y2', cutoffLegendHeight / 2)
                .attr('stroke', '#fff')
                .attr('stroke-width', 7);

            cutoffLegend.append('line')
                .attr('x1', 12)
                .attr('x2', 52)
                .attr('y1', cutoffLegendHeight / 2)
                .attr('y2', cutoffLegendHeight / 2)
                .attr('stroke', infoColor)
                .attr('stroke-width', 5)
                .attr('stroke-linecap', 'round')
                .attr('stroke-dasharray', '1,10');

            cutoffLegend.append('text')
                .attr('x', 62)
                .attr('y', cutoffLegendHeight / 2 + 5)
                .attr('font-size', '14px')
                .attr('fill', '#333')
                .style('font-family', 'Open Sans, Segoe UI, Helvetica Neue, Arial, sans-serif')
                .text('Cutoff Lines');
        }

        // Publish plot geometry so the wheel/drag handlers can map screen pixels
        // to data values without re-running the full draw.
        metricsRef.current = {
            svgWidth: width,
            marginLeft: margin.left,
            plotWidth,
            xDomain: zoomRange,
            outer: dataDomain,
        };

    }, [data, width, height, userXDomain, dataDomain, showCutoffLines]);

    // Mouse-wheel zoom (x only), anchored on the value under the cursor.
    useEffect(() => {
        const svgEl = svgRef.current;
        if (!svgEl) return undefined;
        const onWheel = (event) => {
            const m = metricsRef.current;
            if (!m) return;
            event.preventDefault();
            const rect = svgEl.getBoundingClientRect();
            const scaleX = rect.width ? m.svgWidth / rect.width : 1;
            const px = (event.clientX - rect.left) * scaleX - m.marginLeft;
            const frac = Math.max(0, Math.min(1, px / m.plotWidth));
            const [lo, hi] = m.xDomain;
            const anchor = lo + frac * (hi - lo);
            const factor = event.deltaY > 0 ? 1.15 : 1 / 1.15;
            const nextLo = anchor - (anchor - lo) * factor;
            const nextHi = anchor + (hi - anchor) * factor;
            setUserXDomain(clampXDomain([nextLo, nextHi], m.outer));
        };
        svgEl.addEventListener('wheel', onWheel, { passive: false });
        return () => svgEl.removeEventListener('wheel', onWheel);
    }, [data]);

    // Click-and-drag to pan the x axis. Movement is coalesced to one update per
    // animation frame for smoothness, and skipped when pinned at a boundary.
    useEffect(() => {
        const svgEl = svgRef.current;
        if (!svgEl) return undefined;
        let dragging = false;
        let lastX = 0;
        let pendingDX = 0;
        let rafId = null;

        const flush = () => {
            rafId = null;
            if (!pendingDX) return;
            const m = metricsRef.current;
            if (!m) { pendingDX = 0; return; }
            const span = m.xDomain[1] - m.xDomain[0];
            const dxData = (pendingDX * span) / m.plotWidth;
            pendingDX = 0;
            const next = clampXDomain([m.xDomain[0] - dxData, m.xDomain[1] - dxData], m.outer);
            if (next[0] !== m.xDomain[0] || next[1] !== m.xDomain[1]) setUserXDomain(next);
        };
        const onDown = (event) => {
            dragging = true;
            lastX = event.clientX;
            svgEl.style.cursor = 'grabbing';
            event.preventDefault();
        };
        const onMove = (event) => {
            if (!dragging) return;
            pendingDX += event.clientX - lastX;
            lastX = event.clientX;
            if (rafId === null) rafId = requestAnimationFrame(flush);
        };
        const endDrag = () => {
            if (!dragging) return;
            dragging = false;
            svgEl.style.cursor = 'grab';
            if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; }
            flush();
        };

        svgEl.style.cursor = 'grab';
        svgEl.addEventListener('mousedown', onDown);
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', endDrag);
        return () => {
            svgEl.removeEventListener('mousedown', onDown);
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', endDrag);
            if (rafId !== null) cancelAnimationFrame(rafId);
        };
    }, [data]);

    return (
        <div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
        {(!data || !Array.isArray(data.values) || data.values.length === 0) ? (
            <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: `${height}px`,
                fontSize: '18px',
                color: '#666',
                fontFamily: '"Open Sans", "Segoe UI", "Helvetica Neue", Arial, sans-serif'
            }}>
                No Data Available
            </div>
        ) : (
            <>
                <svg
                    ref={svgRef}
                    width={width}
                    height={height}
                    viewBox={`0 0 ${width} ${height}`}
                    preserveAspectRatio="xMinYMin meet"
                    xmlns="http://www.w3.org/2000/svg"
                />
                <div
                    ref={tooltipRef}
                    style={{
                        position: 'absolute',
                        pointerEvents: 'none',
                        background: '#fff',
                        border: '1px solid #333',
                        borderRadius: 4,
                        padding: '6px 12px',
                        fontSize: 14,
                        fontFamily: '"Open Sans", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
                        color: '#222',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        zIndex: 10,
                        display: 'none'
                    }}
                ></div>
                <Box sx={{ px: 4, pt: 1, pb: 2 }}>
                    <Box component="label" htmlFor="histogram-zoom-slider" sx={{
                        position: 'absolute', width: 1, height: 1, overflow: 'hidden',
                        clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap'
                    }}>
                        Zoom Range
                    </Box>
                    <Slider
                        id="histogram-zoom-slider"
                        min={dataDomain[0]}
                        max={dataDomain[1]}
                        value={clampXDomain(effectiveDomain, dataDomain)}
                        onChange={(_event, value) => setUserXDomain(clampXDomain(value, dataDomain))}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => value.toFixed(2)}
                        step={sliderStep}
                        getAriaLabel={(index) =>
                            index === 0 ? 'Histogram zoom minimum' : 'Histogram zoom maximum'
                        }
                    />
                    <Box sx={{ mt: 1 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<RestartAlt />}
                            onClick={() => setUserXDomain(null)}
                            disabled={!userXDomain}
                        >
                            Reset Zoom
                        </Button>
                    </Box>
                </Box>
            </>
        )}
        </div>
    );
});

export default HistogramChart;